import { Injectable, Inject, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Environment } from '../../environment/environment.model';
import { Observable } from 'rxjs';
import { ChatMessage, ChatReply, ChatStatus, ChatUiMessage } from '../_models/chat.model';
import { WorkflowService } from './workflow.service';

@Injectable({
  providedIn: 'root',
})
export class ChatService {
  // The conversation lives in this root service (not in the step component), so it
  // survives navigating between steps. It resets on a page refresh or when a new
  // workflow is started.
  public messages: ChatUiMessage[] = [];
  public isSending = false;

  private toolLabels: Record<string, string> = {};

  private httpClient = inject(HttpClient);
  private readonly workflowService = inject(WorkflowService);

  constructor(@Inject('env') private environment: Environment) {
    this.workflowService.getReset().subscribe(() => this.clear());
  }

  public getStatus(): Observable<ChatStatus> {
    return this.httpClient.get<ChatStatus>(`${this.environment.apiUrl}/chat`);
  }

  public setToolLabels(labels: Record<string, string>): void {
    this.toolLabels = labels;
  }

  // Sending happens here so an in-flight answer still lands in the conversation
  // when the user switches steps before it arrives.
  public send(question: string): void {
    if (this.isSending) {
      return;
    }

    this.messages.push({ role: 'user', content: question });
    this.isSending = true;

    this.chat(this.buildHistory()).subscribe({
      next: (reply) => {
        this.messages.push({
          role: 'assistant',
          content: reply.reply,
          toolCalls: this.toToolLabels(reply.toolCalls),
        });
        this.isSending = false;
      },
      error: () => {
        this.messages.push({ role: 'assistant', content: '', error: true });
        this.isSending = false;
      },
    });
  }

  public clear(): void {
    this.messages = [];
    this.isSending = false;
  }

  private chat(messages: ChatMessage[]): Observable<ChatReply> {
    return this.httpClient.post<ChatReply>(`${this.environment.apiUrl}/chat`, { messages });
  }

  // The backend is stateless: it receives the full conversation each turn.
  // Error bubbles are UI-only and stay out of the history.
  private buildHistory(): ChatMessage[] {
    return this.messages
      .filter((message) => !message.error)
      .map((message) => ({ role: message.role, content: message.content }));
  }

  private toToolLabels(toolCalls: string[]): string[] {
    return [...new Set(toolCalls)].map((name) => this.toolLabels[name] ?? name);
  }
}
